import { baseApi } from "./baseApi";

// --- Schema item types ---

export interface SidebarNavItem {
  name: string;
  displayName: string;
  icon: string;
  section: string;
  priority: number;
  isVisible: boolean;
  permission?: string;
  adminOnly?: boolean;
  hasTab?: boolean;
  tabUrl?: string;
  dataUrl?: string | null;
  children?: Array<{
    name: string;
    displayName: string;
    icon: string;
    priority: number;
    isVisible: boolean;
    permission?: string;
  }>;
}

export interface TabItem {
  name: string;
  displayName: string;
  dataUrl: string;
  priority: number;
  isVisible: boolean;
  isDefault: boolean;
}

export interface ColumnSchema {
  name: string;
  displayName: string;
  type: string;
  priority: number;
  isVisible: boolean;
  isFilterable?: boolean;
  width?: string;
  format?: string;
  options?: Array<{ value: string; label: string; color?: string }>;
}

// --- Response types ---

export interface SchemaOnlyResponse<T = unknown> {
  displayName: string | null;
  schema: T[];
}

export interface DataResponse<T = unknown> {
  displayName: string | null;
  schema: ColumnSchema[];
  data: Record<string, unknown>[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// --- API slice ---

export const platformConfigApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Generic schema-only fetch (sidebar, tabs, features, etc.)
    getConfigSchema: builder.query<
      SchemaOnlyResponse,
      { category: string; data: string; instanceId: string }
    >({
      query: ({ category, data, instanceId }) =>
        `/${category}/data/${data}?instanceId=${instanceId}&schemaOnly=true`,
      providesTags: (_result, _error, { category, data }) => [
        { type: "PlatformConfig" as const, id: `${category}:${data}` },
      ],
    }),

    // Generic data fetch (table data with schema)
    getConfigData: builder.query<
      DataResponse,
      {
        dataUrl: string;
        instanceId: string;
        page?: number;
        limit?: number;
        search?: string;
        sortBy?: string;
        sortOrder?: "asc" | "desc";
        filters?: Record<string, string>;
      }
    >({
      query: ({
        dataUrl,
        instanceId,
        page = 1,
        limit = 25,
        search,
        sortBy,
        sortOrder,
        filters,
      }) => {
        // dataUrl is like "/api/v1/lead_sales/data/table" — strip the /api/v1 prefix
        // since baseApi already adds it
        const path = dataUrl.replace(/^\/api\/v1/, "");
        const params = new URLSearchParams();
        params.set("instanceId", instanceId);
        params.set("page", String(page));
        params.set("limit", String(limit));
        if (search) params.set("search", search);
        if (sortBy) params.set("sortBy", sortBy);
        if (sortOrder) params.set("sortOrder", sortOrder);
        if (filters) {
          for (const [k, v] of Object.entries(filters)) {
            params.set(k, v);
          }
        }
        return `${path}?${params.toString()}`;
      },
      providesTags: (_result, _error, { dataUrl }) => [
        { type: "PlatformConfig" as const, id: `data:${dataUrl}` },
      ],
    }),

    // Update config (for admin settings)
    updateConfig: builder.mutation<
      unknown,
      { category: string; data: string; instanceId: string; updates: Record<string, unknown> }
    >({
      query: ({ category, data, instanceId, updates }) => ({
        // instanceId rides the query string too: RBAC middleware
        // (requireAdmin/requireRole) resolves the instance from
        // req.query.instanceId only.
        url: `/${category}/data/${data}?instanceId=${instanceId}`,
        method: "PUT",
        body: { instanceId, ...updates },
      }),
      invalidatesTags: (_result, _error, { category, data }) => [
        { type: "PlatformConfig" as const, id: `${category}:${data}` },
      ],
    }),
  }),
});

export const { useGetConfigSchemaQuery, useGetConfigDataQuery, useUpdateConfigMutation } =
  platformConfigApi;
